import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { X, Globe, UserPlus, Users, Broadcast, PaperPlaneTilt } from '@phosphor-icons/react';

/**
 * Modal: pick where the Bubble drifts. Friend / Tribe / Beacon / World.
 */
const DriftCompose = ({ bubble, open, onClose, onSent }) => {
  const [dest, setDest] = useState('world');
  const [friendUsername, setFriendUsername] = useState('');
  const [tribeId, setTribeId] = useState('');
  const [beaconCode, setBeaconCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const body = { destination: dest };
      if (dest === 'friend') body.friend_username = friendUsername.trim();
      if (dest === 'tribe') body.tribe_id = tribeId.trim();
      if (dest === 'beacon') body.beacon_code = beaconCode.trim().toUpperCase();
      const { data } = await api.post(`/bubbles/${bubble.id}/drift`, body);
      toast.success(`${bubble.name} is drifting to ${data.drift.destination_label}`, {
        description: 'You\'ll hear back when it lands and again when it returns.',
      });
      onSent?.(data);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'could not send');
    } finally { setSubmitting(false); }
  };

  const canSubmit = (() => {
    if (dest === 'friend') return friendUsername.trim().length > 0;
    if (dest === 'tribe') return tribeId.trim().length > 0;
    if (dest === 'beacon') return beaconCode.trim().length >= 4;
    return true;
  })();

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="drift-compose"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-xl grid place-items-center px-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 20, scale: 0.96 }} animate={{ y: 0, scale: 1 }} exit={{ y: 20, scale: 0.96 }}
            className="w-full max-w-md glass rounded-3xl p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button data-testid="drift-close" onClick={onClose}
              className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 text-white/60">
              <X size={18} weight="bold" />
            </button>
            <div className="text-center mb-5">
              <div className="text-[10px] uppercase tracking-widest text-bx-magenta">message in a bottle</div>
              <div className="font-display font-bold text-xl mt-1">Send {bubble.name} drifting</div>
              <div className="text-xs text-white/65 mt-1">
                Your Bubble travels with its cargo, lands somewhere, and brings the reaction back.
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-4">
              {[
                { k: 'world',  icon: Globe,     label: 'The world',  hint: 'a random stranger' },
                { k: 'friend', icon: UserPlus,  label: 'A friend',   hint: 'by @username' },
                { k: 'tribe',  icon: Users,     label: 'My tribe',   hint: 'one member, at random' },
                { k: 'beacon', icon: Broadcast, label: 'A beacon',   hint: 'a real-world QR portal' },
              ].map((opt) => (
                <button key={opt.k}
                  data-testid={`drift-dest-${opt.k}`}
                  onClick={() => setDest(opt.k)}
                  className={`rounded-2xl p-3 text-left border transition ${
                    dest === opt.k
                      ? 'border-bx-magenta bg-bx-magenta/10'
                      : 'border-white/10 hover:border-white/30'
                  }`}>
                  <div className="flex items-center gap-2">
                    <opt.icon size={16} weight="duotone"
                      className={dest === opt.k ? 'text-bx-magenta' : 'text-white/65'} />
                    <span className="text-sm font-bold">{opt.label}</span>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">
                    {opt.hint}
                  </div>
                </button>
              ))}
            </div>

            {dest === 'friend' && (
              <input
                data-testid="drift-friend"
                value={friendUsername}
                onChange={(e) => setFriendUsername(e.target.value.replace(/^@/, ''))}
                placeholder="@username"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-bx-magenta"
              />
            )}
            {dest === 'tribe' && (
              <input
                data-testid="drift-tribe"
                value={tribeId}
                onChange={(e) => setTribeId(e.target.value)}
                placeholder="tribe id"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-bx-magenta"
              />
            )}
            {dest === 'beacon' && (
              <input
                data-testid="drift-beacon"
                value={beaconCode}
                onChange={(e) => setBeaconCode(e.target.value.toUpperCase())}
                placeholder="beacon code (e.g. WAY3MG)"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-bx-magenta font-mono uppercase"
              />
            )}

            <button
              data-testid="drift-submit"
              disabled={!canSubmit || submitting}
              onClick={submit}
              className={`mt-5 w-full py-3 rounded-full font-bold text-sm flex items-center justify-center gap-2 transition ${
                canSubmit && !submitting ? 'btn-volt' : 'bg-white/5 text-white/55 cursor-not-allowed'
              }`}>
              <PaperPlaneTilt size={16} weight="fill" />
              {submitting ? 'sending…' : 'Drift'}
            </button>
            <div className="text-[10px] text-white/55 text-center mt-2">
              Lands within minutes. Returns within 24–72h with whatever it found.
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DriftCompose;
